import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useBetaData } from '../../contexts/BetaDataContext';
import ProjectCreateModal from './projects/ProjectCreateModal';
import { isNewWorkItemUI, isAfterCutover } from '../../constants/cutover';
import './BetaSidebar.scss';

const BetaSidebar = ({
  currentView,
  selectedProjectId,
  collapsed,
  mobileOpen,
  onNavigate,
  onToggleCollapse,
  width,
  onResizeStart,
}) => {
  const {
    isSuperAdmin,
    isCompanyAdmin,
  } = useAuth();
  const { projects: betaProjects } = useBetaData();
  const [searchParams] = useSearchParams();
  const isNewUI = isNewWorkItemUI(searchParams);
  const cutoverPassed = isAfterCutover();
  const [showViewsMenu, setShowViewsMenu] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateProject, setShowCreateProject] = useState(false);
  const [editingProject, setEditingProject] = useState(null);

  const filteredProjects = (betaProjects || []).filter((p) =>
    p.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const sidebarClass = [
    'beta-sidebar',
    collapsed ? 'collapsed' : '',
    mobileOpen ? 'mobile-open' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={sidebarClass} style={width ? { width } : undefined}>
      {/* Resize handle */}
      {!collapsed && onResizeStart && (
        <div
          className="sidebar-resize-handle"
          onMouseDown={onResizeStart}
        />
      )}
      {/* Search */}
      {!collapsed && (
        <div className="beta-sidebar-search">
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            stroke="#999"
            strokeWidth="2"
          >
            <circle cx="7" cy="7" r="5" />
            <path d="M11 11l3.5 3.5" strokeLinecap="round" />
          </svg>
          <input
            type="text"
            placeholder="搜尋..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      )}

      {/* Scrollable content area */}
      <div className="beta-sidebar-scroll">

      {/* My Tasks */}
      <div className="beta-sidebar-section">
        <div
          className={`beta-sidebar-item ${currentView === 'my-tasks' ? 'active' : ''}`}
          onClick={() => onNavigate('my-tasks')}
          data-tour="my-tasks"
        >
          <span className="item-icon">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="2" y="2" width="14" height="14" rx="2" />
              <path d="M6 9l2 2 4-4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          {!collapsed && <span className="item-label">{isNewUI ? '我的工作項目' : '我的任務'}</span>}
        </div>
        <div
          className={`beta-sidebar-item ${currentView === 'global-search' ? 'active' : ''}`}
          onClick={() => onNavigate('global-search')}
        >
          <span className="item-icon">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="8" cy="8" r="5.5" />
              <path d="M12.5 12.5l3.5 3.5" strokeLinecap="round" />
            </svg>
          </span>
          {!collapsed && <span className="item-label">{isNewUI ? '全系統工作項目查詢' : '全系統 Ticket 查詢'}</span>}
        </div>
        {/* 轉換後(全新版)過往 Ticket 仍可查閱:新版列表底下多開「全系統 Ticket 查詢」入口 */}
        {isNewUI && cutoverPassed && (
          <div
            className={`beta-sidebar-item ${currentView === 'global-ticket-search' ? 'active' : ''}`}
            onClick={() => onNavigate('global-ticket-search')}
          >
            <span className="item-icon">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="2.5" y="3" width="13" height="12" rx="2" />
                <path d="M2.5 7h13M6 11h6" strokeLinecap="round" />
              </svg>
            </span>
            {!collapsed && <span className="item-label">全系統 Ticket 查詢</span>}
          </div>
        )}
      </div>

      {/* Views Menu — above project list */}
      <div className="beta-sidebar-section" data-tour="views-menu">
        {!collapsed && (
          <div
            className="section-title clickable"
            onClick={() => setShowViewsMenu(!showViewsMenu)}
          >
            視圖
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              className={`chevron ${showViewsMenu ? 'open' : ''}`}
            >
              <path d="M3 5l3 3 3-3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        )}
        {(showViewsMenu || collapsed) && (
          <div className="views-list">
            {[
              { key: 'timeline', label: 'Timeline', iconSvg: <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="1" y="3" width="6" height="3" rx="1" /><rect x="4" y="7" width="8" height="3" rx="1" /><rect x="2" y="11" width="5" height="3" rx="1" /></svg> },
              { key: 'board', label: 'Board', iconSvg: <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="1" y="1" width="4" height="14" rx="1" /><rect x="6" y="1" width="4" height="10" rx="1" /><rect x="11" y="1" width="4" height="7" rx="1" /></svg> },
              { key: 'milestone', label: 'Milestone', iconSvg: <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 14V2" strokeLinecap="round" /><path d="M3 3h8l-2 3 2 3H3" /></svg> },
              { key: 'calendar', label: 'Calendar', iconSvg: <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="2" y="3" width="12" height="11" rx="1.5" /><path d="M2 6.5h12M5.5 1.5v3M10.5 1.5v3" strokeLinecap="round" /></svg> },
            ]
              // 新版:Timeline / Milestone 屬舊版 Ticket/Phase 概念,時間維度改由 Calendar 取代,故只留 Board + Calendar;舊版不顯示 Calendar
              .filter((view) => (isNewUI ? (view.key === 'board' || view.key === 'calendar') : view.key !== 'calendar'))
              .map((view) => (
              <div
                key={view.key}
                className={`beta-sidebar-item ${currentView === view.key ? 'active' : ''}`}
                onClick={() => onNavigate(view.key)}
              >
                <span className="item-icon">{view.iconSvg}</span>
                {!collapsed && <span className="item-label">{view.label}</span>}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Project List */}
      <div className="beta-sidebar-section" data-tour="project-list">
        {!collapsed && (
          <div className="section-title section-title-with-action">
            <span>專案</span>
            {(isSuperAdmin() || isCompanyAdmin()) && (
              <button
                className="section-add-btn"
                onClick={() => setShowCreateProject(true)}
                title="建立專案"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M7 2v10M2 7h10" strokeLinecap="round" />
                </svg>
              </button>
            )}
          </div>
        )}
        <div className="project-list">
          {filteredProjects.length === 0 && !collapsed && (
            <div className="empty-hint">尚無專案</div>
          )}
          {filteredProjects.map((project) => (
            <div
              key={project.id}
              className={`beta-sidebar-item ${
                currentView === 'project' && selectedProjectId === project.id
                  ? 'active'
                  : ''
              }`}
              onClick={() => onNavigate('project', project.id)}
            >
              <span
                className="project-dot"
                style={{ backgroundColor: project.color || '#667eea' }}
              />
              {!collapsed && <span className="item-label">{project.name}</span>}
              {!collapsed && (isCompanyAdmin() || isSuperAdmin()) && (
                <button
                  className="project-edit-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditingProject(project);
                  }}
                  title="編輯專案"
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <circle cx="7" cy="3" r="0.8" fill="currentColor" stroke="none" />
                    <circle cx="7" cy="7" r="0.8" fill="currentColor" stroke="none" />
                    <circle cx="7" cy="11" r="0.8" fill="currentColor" stroke="none" />
                  </svg>
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      </div>{/* end beta-sidebar-scroll */}

      {/* Project Create Modal */}
      <ProjectCreateModal
        isOpen={showCreateProject}
        onClose={() => setShowCreateProject(false)}
        onSuccess={(project) => onNavigate('project', project.id)}
      />

      {/* Project Edit Modal */}
      <ProjectCreateModal
        isOpen={!!editingProject}
        project={editingProject}
        onClose={() => setEditingProject(null)}
        onSuccess={(result) => {
          setEditingProject(null);
          if (result === null) {
            // 已刪除 → 回到我的任務
            onNavigate('my-tasks');
          }
        }}
      />

    </div>
  );
};

export default BetaSidebar;
